import type { JSX } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

/**
 * Plan 12-03 — RejectionBanner
 *
 * Read-only destructive-styled banner shown at the top of the Edit/Detail
 * pages when `entry.status === 'Rejected'`. Honors D-107: rejected entries
 * surface the rejection reason + checker identity at the top of the form
 * so the maker can correct and resubmit.
 *
 * Renders nothing if `rejectionReason` is empty; this lets the parent pass
 * unconditional props from the detail entry without a guard.
 */

export interface RejectionBannerProps {
  readonly rejectionReason: string;
  readonly rejectedByUserName: string | null;
  readonly rejectedAt: string | null;
}

function formatLocale(iso: string | null): string {
  if (iso === null || iso === '') return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function RejectionBanner({
  rejectionReason,
  rejectedByUserName,
  rejectedAt,
}: RejectionBannerProps): JSX.Element | null {
  if (rejectionReason.trim() === '') return null;
  const meta: string[] = [];
  if (rejectedByUserName !== null && rejectedByUserName !== '') {
    meta.push(`Rejected by ${rejectedByUserName}`);
  }
  const formatted = formatLocale(rejectedAt);
  if (formatted !== '') meta.push(`on ${formatted}`);
  return (
    <Alert variant="destructive" data-testid="rejection-banner">
      <AlertTitle>Entry was rejected</AlertTitle>
      <AlertDescription>
        <p data-testid="rejection-banner-reason">{rejectionReason}</p>
        {meta.length > 0 && (
          <p className="text-xs opacity-80" data-testid="rejection-banner-meta">
            {meta.join(' ')}
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}
